import { RISK_BANDS, riskBand, type RiskBand } from '@forecastify/shared';
import { Tooltip } from '@forecastify/ui';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C60 · RISK, wherever it is drawn. One score, one set of colours.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE BAND DECIDES THE COLOUR AND `@shared` DECIDES THE BAND.** `riskBand()` holds the three
 * boundaries — 85 and 60 — and they are also the projects list's filter options and the *Adathiba*
 * view's threshold. A second copy of *"green above 85"* here is how a pill and a filter end up
 * disagreeing about the same project.
 *
 * ⚠️ All three are tokens — `--status-warn` is the same amber as the severity dot in
 * `alert-text.ts`.
 */
export const RISK_TONE: Record<RiskBand, string> = {
  ok: 'var(--status-ok)',
  watch: 'var(--status-warn)',
  risky: 'var(--destructive)',
};

export function riskLabel(t: Translate, score: number): string {
  return t(`risk.band.${riskBand(score)}`);
}

/**
 * ⚠️⚠️ **MUTED ABOVE 85, AND THE MOCKUP IS RIGHT TO DO THAT.** A column of confident green numbers
 * competes with the two rows that need attention; a healthy project's score is a reassurance, not a
 * finding. The dot stays — geometry, not colour alone — at reduced weight.
 */
export function riskDot(score: number): string {
  const tone = RISK_TONE[riskBand(score)];
  return score >= 85 ? `color-mix(in oklch, ${tone} 55%, transparent)` : tone;
}

/**
 * The tooltip, which is where the score stops being a number and becomes an explanation.
 *
 * ⚠️⚠️ **IT NAMES THE DEDUCTION AND THE CAP, BECAUSE A BARE 64 IS UNACTIONABLE.** The mockup's own
 * `riskTip` reads *"RISK 64 · figyelendő — 36 pont levonás 9 nyitott alertből"*, and without that
 * sentence the reader's only route to *why* is to open the project and count. This is the shorter
 * version the list can carry: the band, and where to go for the rest.
 *
 * ⚠️ **`null` gets its own sentence rather than a blank tooltip.** *Not yet scanned* is the one
 * thing a reader cannot guess from an em-dash.
 */
export function riskTitle(t: Translate, score: number | null): string {
  if (score === null) return t('risk.unscanned');
  return t('risk.tip', { score: String(score), band: riskLabel(t, score) });
}

/**
 * ⚠️ **The pill for the project header** — the mockup draws it top-right beside the kebab, which is
 * `phase5/03-project-alerts.png`'s amber `• 60`.
 */
export function RiskPill({ t, score }: { readonly t: Translate; readonly score: number | null }) {
  /* ⚠️ Nothing at all rather than an em-dash pill: in the header the score is an aside, and a
     header ornament that says "we have not checked" is noise on every unscanned project. The
     LIST draws the dash, because there the column has to line up. */
  if (score === null) return null;

  const tone = RISK_TONE[riskBand(score)];
  return (
    /**
     * ⚠️⚠️ **`Tooltip`, NEVER `title=`.** A native tooltip is drawn by the operating system: it
     * takes no styling, appears on its own schedule, sits in a different font and cannot be
     * reached by keyboard. The same reason `Select` is a themed listbox rather than a bare
     * `<select>` — the platform default looks adjacent to the product rather than part of it.
     */
    <Tooltip content={riskTitle(t, score)} width={280}>
      <span
        className="inline-flex items-center gap-1.5 rounded-full border px-2 py-[2px] text-[12px] tabular-nums"
        style={{
          color: tone,
          borderColor: `color-mix(in oklch, ${tone} 40%, transparent)`,
          background: `color-mix(in oklch, ${tone} 10%, transparent)`,
        }}
      >
        <span
          aria-hidden="true"
          className="inline-block size-[6px] rounded-full"
          style={{ background: tone }}
        />
        {/* ⚠️ Named for a screen reader, which gets no colour and no tooltip. */}
        <span className="sr-only">{t('risk.label')} </span>
        {score}
      </span>
    </Tooltip>
  );
}

/** ⚠️ The filter's four options, so the list and any other caller order the bands identically. */
export const RISK_FILTER_VALUES = ['bad', 'warn', 'ok'] as const;

/** ⚠️ Derived from `RISK_BANDS` so a boundary change reaches the labels rather than only the maths. */
export const RISK_BOUNDS = {
  ok: RISK_BANDS[0].min,
  watch: RISK_BANDS[1].min,
} as const;
